/**
 * Audit-log client for the Sprint BD endpoints — Sprint BN.
 *
 * The `/audit` endpoint (Sprint BD) adds a `q` substring filter
 * and a CSV-export companion that the older
 * `/observability/audit-log` doesn't have. Keep both — the old
 * endpoint stays the default for the paginated table; this one
 * powers the substring-search + download paths.
 */

import { api, API_BASE_URL, type RequestOptions } from "./client";


export interface AuditRowDto {
  id:           string;
  occurred_at:  string;
  actor_type:   string;
  actor_id:     string;
  action:       string;
  target_type:  string | null;
  target_id:    string | null;
  result:       string;
  client_ip:    string | null;
  details:      Record<string, unknown>;
}


export interface AuditSearchFilters {
  actor?:        string;
  action?:       string;
  target_type?:  string;
  result?:       string;
  from?:         string;
  to?:           string;
  q?:            string;
  limit?:        number;
  offset?:       number;
}


function _qs(f: AuditSearchFilters): string {
  const params = new URLSearchParams();
  if (f.actor)        params.set("actor",       f.actor);
  if (f.action)       params.set("action",      f.action);
  if (f.target_type)  params.set("target_type", f.target_type);
  if (f.result)       params.set("result",      f.result);
  if (f.from)         params.set("from",        f.from);
  if (f.to)           params.set("to",          f.to);
  if (f.q)            params.set("q",           f.q);
  if (f.limit  !== undefined) params.set("limit",  String(f.limit));
  if (f.offset !== undefined) params.set("offset", String(f.offset));
  return params.toString();
}


export const auditApi = {
  search(
    filters: AuditSearchFilters = {},
    opts?: RequestOptions,
  ): Promise<AuditRowDto[]> {
    const q = _qs(filters);
    return api.get<AuditRowDto[]>(
      q ? `/audit?${q}` : "/audit", opts,
    );
  },

  /** Fetch the CSV stream with the JWT in the Authorization header,
   *  then trigger a download via a blob URL. We can't use a bare
   *  `<a href>` because the browser can't attach headers to it,
   *  and the backend deliberately doesn't accept query-string tokens
   *  (they leak via Referer, server logs, and history). */
  async downloadCsv(
    filters: AuditSearchFilters = {},
    filename = "audit_log.csv",
  ): Promise<void> {
    const q = _qs(filters);
    const url = `${API_BASE_URL}/audit/export.csv${q ? `?${q}` : ""}`;
    const token = (() => {
      try { return localStorage.getItem("vseh.access_token") ?? ""; }
      catch { return ""; }
    })();
    // exactOptionalPropertyTypes — pass headers only when set.
    const init: RequestInit = {};
    if (token) init.headers = { Authorization: `Bearer ${token}` };
    const res = await fetch(url, init);
    if (!res.ok) {
      throw new Error(`CSV export failed: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },
};
